import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  LocalizationProvider,
  DateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { BACKEND_BASE_URL } from "../config";
import moment from "moment-timezone";
import api from '../components/api';

export default function AddNotification({ onNotificationAdded }) {
  const [type, setType] = useState("");
  const [context, setContext] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [schedule, setSchedule] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [sendOption, setSendOption] = useState("now");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [subject, setSubject] = useState(""); // State for subject
  

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(`${BACKEND_BASE_URL}/api/users-list/`);
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users.");
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!type) newErrors.type = "Type is required.";
    if (!context.trim()) newErrors.context = "Context is required.";
    if (!subject.trim()) newErrors.subject = "Subject is required.";
    if (recipients.length === 0) newErrors.recipients = "At least one recipient is required.";
    if (sendOption === "later" && new Date(schedule) <= new Date()) {
      newErrors.schedule = "Scheduled time must be in the future.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
  
    if (!validateFields()) {
      toast.error("Please correct the errors before submitting.");
      return;
    }
    
    // Get the current time adjusted slightly into the future
    const currentTime = moment().add(30, "seconds").format(); // Add 5 seconds to the current time
  
    const payload = {
      type,
      context,
      subject,
      recipients: recipients.includes("all") ? users.map((user) => user.id) : recipients,
      // ...(sendOption !== "now" && { schedule: moment(schedule).format() }),
      // schedule: sendOption === "now" ? currentTime : moment(schedule).format(), // Use adjusted current time or scheduled time
    };
   
    if (sendOption === "later") {
      payload.schedule = moment(schedule).format(); // Add schedule only for "later"
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.post(`${BACKEND_BASE_URL}/api/notifications/`, payload);
      toast.success("Notification created successfully.");
      resetForm();
      if (onNotificationAdded) {
        onNotificationAdded(response.data);
      }
    } catch (error) {
      console.error("Error creating notification:", error);
      toast.error("Failed to create notification.");
    } finally {
      setLoading(false);
    }
  };
  
  
  

  const getRecipientLabel = (user) => {
    if (!user) return "Unknown User";
    if (type === "email") return `${user.name} (${user.email || "N/A"})`;
    if (type === "whatsapp") return `${user.name} (${user.phone_number || "N/A"})`;
    return user.name;
  };

  const resetForm = () => {
    setType("");
    setContext("");
    setRecipients([]);
    setSchedule(new Date());
    setSendOption("now");
    setSubject(""); // Reset subject
    setErrors({});
  };

  return (
    <Box
      sx={{
        maxWidth: 900,
        margin: "40px auto",
        padding: 4,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Create Notification
      </Typography>
      <Grid container spacing={3}>
        {/* Type Field */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Type *</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              error={!!errors.type}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
            {errors.type && <Typography color="error" variant="caption">{errors.type}</Typography>}
          </FormControl>
        </Grid>

        {/* Subject Field */}
        <Grid item xs={12}>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
            error={!!errors.subject}
            helperText={errors.subject}
          />
        </Grid>

        {/* Context Field */}
        <Grid item xs={12}>
          <Typography variant="h6">Context *</Typography>
          <ReactQuill
            theme="snow"
            value={context}
            onChange={setContext}
            style={{ minHeight: "150px" }}
          />
          {errors.context && <Typography color="error" variant="caption">{errors.context}</Typography>}
        </Grid>

        {/* Recipients Field */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Recipients *</InputLabel>
            <Select
              multiple
              value={recipients}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes("all")) {
                  setRecipients(["all"]);
                } else {
                  setRecipients(value.filter((v) => v !== "all"));
                }
              }}
              renderValue={(selected) =>
                selected.includes("all")
                  ? "All Users"
                  : selected.map((id) => users.find((u) => u.id === id)?.name || id).join(", ")
              }
            >
              <MenuItem value="all">
                <Checkbox checked={recipients.includes("all")} />
                <ListItemText primary="All Users" />
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id} disabled={recipients.includes("all")}>
                  <Checkbox checked={recipients.includes(user.id)} />
                  <ListItemText primary={getRecipientLabel(user)} />
                </MenuItem>
              ))}
            </Select>
            {errors.recipients && <Typography color="error" variant="caption">{errors.recipients}</Typography>}
          </FormControl>
        </Grid>

        {/* Send Option Field */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel>Send Option *</FormLabel>
            <RadioGroup
              row
              value={sendOption}
              onChange={(e) => setSendOption(e.target.value)}
            >
              <FormControlLabel value="now" control={<Radio />} label="Send Now" />
              <FormControlLabel value="later" control={<Radio />} label="Schedule for Later" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Schedule Field */}
        {sendOption === "later" && (
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Schedule"
                value={schedule}
                onChange={setSchedule}
                minDate={new Date()}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            {errors.schedule && <Typography color="error" variant="caption">{errors.schedule}</Typography>}
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Create Notification"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

